import { AppShell } from '@mantine/core';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { render, renderHook } from '@testing-library/react';
import { createMemoryHistory, History } from 'history';
import i18n from 'i18next';
import { I18nextProvider } from 'react-i18next';
import { Router } from 'react-router-dom';
import { QueryParamProvider } from 'use-query-params';
import { ReactRouter6Adapter } from 'use-query-params/adapters/react-router-6';
import translation from '../../public/locales/en/translation.json';
import { MantineProvider } from '@mantine/core';
import { mantineTheme } from './theme/theme';

i18n.init({
  resources: { en: { translation } },
  fallbackLng: 'en',
});

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: false, cacheTime: 0, staleTime: 0 },
  },
});

type TestingProvidersProps = {
  routerEntries?: string[];
  children?: React.ReactNode;
  history?: History;
};

export const TestingProviders = ({
  children,
  history = createMemoryHistory(),
}: TestingProvidersProps) => (
  <I18nextProvider i18n={i18n}>
    <MantineProvider theme={mantineTheme} defaultColorScheme="dark">
      <AppShell>
        <Router location={history.location} navigator={history}>
          <QueryParamProvider adapter={ReactRouter6Adapter}>
            <QueryClientProvider client={queryClient}>
              {children}
            </QueryClientProvider>
          </QueryParamProvider>
        </Router>
      </AppShell>
    </MantineProvider>
  </I18nextProvider>
);

const customRender = (
  ui: React.ReactElement,
  testProvidersProps?: TestingProvidersProps
) => {
  return render(ui, {
    wrapper: ({ children }) => (
      <TestingProviders {...testProvidersProps}>{children}</TestingProviders>
    ),
  });
};

const customRenderHook = <TResult, TProps = unknown>(
  hook: Parameters<typeof renderHook<TResult, TProps>>[0],
  testProvidersProps?: TestingProvidersProps
) => {
  return renderHook<TResult, TProps>(hook, {
    wrapper: ({ children }) => (
      <TestingProviders {...testProvidersProps}>{children}</TestingProviders>
    ),
  });
};

export * from '@testing-library/react';

// override @testing-library methods
export { customRender as render, customRenderHook as renderHook };
